import { CommonModule } from "@angular/common";
import { BreakpointObserver } from "@angular/cdk/layout";
import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslocoService } from "@jsverse/transloco";
import { catchError, EMPTY, filter, map, switchMap, take } from "rxjs";
import { FuseAlertModule } from "@vui/alert";
import { UserServiceProxy } from "@api/core";
import { TranscriptionAlertDismissalDto } from "@api/core.models";
import { AppSessionService } from "@vadmin/account/auth";
import { FeatureUsageEventsService } from "@subscription/feature-usage-events";
import { TranscriptionUsageService } from "./transcription-usage.service";
import { isTranscriptionAlertSnoozed } from "./transcription-alert-dismissal";
import { tenantTranscriptionEnabled } from "../transcription-eligibility-helpers";
import { TRANSCRIPTION_FREE_TRIAL_LIMIT, TRANSCRIPTION_MONTHLY_LIMIT_FEATURE } from "../transcription.model";

/** Surface the nudge once fewer than this many transcriptions remain, regardless of quota size, so a
 *  small free-tier quota (e.g. 3/month) prompts the upsell from the start — not only at the
 *  proportional NEAR_LIMIT_RATIO slice, which for tiny quotas only triggers with 1 left. */
const LOW_QUOTA_THRESHOLD = 5;

const TOOLBAR_ALERT_MIN_WIDTH = "(min-width: 1024px)";

@Component({
    selector: "transcription-usage-alert",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButtonModule, MatIconModule, MatTooltipModule, FuseAlertModule, CommonModule],
    templateUrl: "./transcription-usage-alert.component.html",
    providers: [TranscriptionUsageService, UserServiceProxy],
})
export class TranscriptionUsageAlertComponent implements OnInit {
    private readonly _breakpoints = inject(BreakpointObserver);
    private readonly _router = inject(Router);
    private readonly _session = inject(AppSessionService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _usageEvents = inject(FeatureUsageEventsService);
    private readonly _userProxy = inject(UserServiceProxy);
    private readonly _t = inject(TranslocoService);
    readonly usage = inject(TranscriptionUsageService);

    @Input() mode: "toolbar" | "panel" = "toolbar";

    @Input() dismissible = true;

    // Admins (clinic managers and tenant owners) manage transcriptions at the subscription level
    // (/user-features/transcription); only a non-admin doctor self-contracts via checkout.
    // Roles are fixed per session, so this evaluates once.
    readonly isIndividualDoctor = computed(() => this._session.isDoctor() && !this._session.isAdmin());

    private readonly _dismissed = signal(false);

    private readonly _tenantHasTranscription = tenantTranscriptionEnabled();

    private readonly _tenantHasTranscription$ = toObservable(this._tenantHasTranscription);

    readonly viewportFitsAlert = toSignal(
        this._breakpoints.observe(TOOLBAR_ALERT_MIN_WIDTH).pipe(map((state) => state.matches)),
        { initialValue: this._breakpoints.isMatched(TOOLBAR_ALERT_MIN_WIDTH) }
    );

    constructor() {
        this._session.onTenantInfoChanged.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
            if (this._tenantHasTranscription()) {
                this.usage.reload();
            }
        });

        this._usageEvents.acquired$
            .pipe(
                filter((feature) => feature === TRANSCRIPTION_MONTHLY_LIMIT_FEATURE),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe(() => this.usage.reload());
    }

    ngOnInit(): void {
        // The escalating snooze governs only the global (toolbar) alert; the in-context (panel) alert
        // always shows for an eligible tenant — "o alerta fica só no contexto de Transcrições".
        if (this.mode !== "toolbar" || !this.usage.featureActive() || this.usage.isUnlimited()) {
            return;
        }
        this._tenantHasTranscription$
            .pipe(
                filter(Boolean),
                take(1),
                switchMap(() => this._userProxy.getTranscriptionAlertDismissal()),
                // A failed fetch hides the alert rather than risking a spurious/duplicate nudge.
                catchError(() => {
                    this._dismissed.set(true);
                    return EMPTY;
                }),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe((state) => this._dismissed.set(this._isSnoozed(state)));
    }

    private readonly _low = computed(() => this.usage.isNearLimit() || this.usage.remaining() < LOW_QUOTA_THRESHOLD);

    /** The trial (limit === 3) is over once its quota is consumed; a paid pack keeps nudging near/over limit. */
    private readonly _freeTrialExhausted = computed(
        () => this.usage.limit() === TRANSCRIPTION_FREE_TRIAL_LIMIT && this.usage.isExhausted()
    );

    readonly showExhaustedUpsell = computed(() => this.mode === "panel" && this._freeTrialExhausted());

    readonly visible = computed(() => {
        if (
            this._dismissed() ||
            !this._tenantHasTranscription() ||
            !this.usage.featureActive() ||
            this.usage.isUnlimited() ||
            !this.usage.loaded()
        ) {
            return false;
        }
        if (this._freeTrialExhausted()) {
            return this.showExhaustedUpsell();
        }
        return this.mode === "panel" || this._low();
    });

    readonly alertType = computed(() => (this._low() && !this.showExhaustedUpsell() ? "warn" : "info"));
    readonly alertIcon = computed(() =>
        this.showExhaustedUpsell() ? "auto_awesome" : this._low() ? "warning" : "info"
    );

    readonly message = computed(() =>
        this.showExhaustedUpsell()
            ? this._t.translate("patient-transcriptions.quota-exhausted-cta")
            : this.usage.quotaLabel()
    );

    readonly supportHint = computed(() =>
        this._low() && !this.showExhaustedUpsell()
            ? this._t.translate("patient-transcriptions.quota-contact-support")
            : null
    );

    readonly compactLabel = computed(() => [this.message(), this.supportHint()].filter(Boolean).join(" — "));

    readonly compactIconColor = computed(() => (this._low() ? "warn" : "primary"));

    navigate(): void {
        if (this._dismissed()) {
            return;
        }
        if (this.isIndividualDoctor()) {
            const url = this._router.serializeUrl(
                this._router.createUrlTree(["/subscription/feature-checkout/transcriptions"])
            );
            window.open(url, "_blank");
            return;
        }
        this._router.navigate(["/user-features/transcription"]);
    }

    onDismissed(): void {
        this._dismissed.set(true);
        if (this.mode !== "toolbar") {
            return;
        }
        this._userProxy
            .dismissTranscriptionAlert()
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe();
    }

    private _isSnoozed(state: TranscriptionAlertDismissalDto): boolean {
        const lastDismissedAt = state?.lastDismissedAt ? state.lastDismissedAt.valueOf() : 0;
        return isTranscriptionAlertSnoozed(state?.count ?? 0, lastDismissedAt, Date.now());
    }
}
